// src/pages/Tournaments.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

const COPPER = "#B87333";

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(date) {
  if (!date) return "—";

  if (typeof date?.toDate === "function") {
    return date.toDate().toLocaleDateString();
  }

  try {
    return new Date(date).toLocaleDateString();
  } catch {
    return "—";
  }
}

function computeFallbackPrizePool(tournament) {
  const entrantsCount = Number(tournament?.entrantsCount || 0);
  const buyIn = Number(tournament?.buyIn || 0);

  const totalRebuys = Number(tournament?.totalRebuys || 0);
  const totalAddOns = Number(tournament?.totalAddOns || 0);
  const addOnCost = Number(tournament?.addOnCost || 0);

  const base = entrantsCount * buyIn;
  const rebuyMoney = totalRebuys * buyIn;
  const addOnMoney = totalAddOns * addOnCost;

  return base + rebuyMoney + addOnMoney;
}

function getCustomChopFirstPlacePrize(tournament) {
  const completionType = String(
    tournament?.results?.completionType || ""
  ).toLowerCase();

  if (completionType !== "custom_chop") return 0;

  const placements = Array.isArray(tournament?.results?.placements)
    ? tournament.results.placements
    : [];

  const firstFromResults = placements.find(
    (row) => Number(row?.finishPlace || row?.place || 0) === 1
  );

  if (firstFromResults) {
    const amount = Number(
      firstFromResults?.negotiatedPayout ?? firstFromResults?.amount ?? 0
    );
    if (amount > 0) return amount;
  }

  const participants = Array.isArray(tournament?.customChop?.participants)
    ? tournament.customChop.participants
    : [];

  const firstFromChop = participants.find(
    (row) => Number(row?.finishPlace || row?.place || 0) === 1
  );

  if (firstFromChop) {
    const amount = Number(
      firstFromChop?.negotiatedPayout ?? firstFromChop?.amount ?? 0
    );
    if (amount > 0) return amount;
  }

  return 0;
}

function computeFirstPlacePrize(tournament) {
  // For a completed custom chop, the negotiated payout is the official result.
  // Do not reconstruct first place from the old scheduled payout percentages.
  const customChopFirst = getCustomChopFirstPlacePrize(tournament);
  if (customChopFirst > 0) return customChopFirst;

  const storedFirstPlacePrize = Number(tournament?.firstPlacePrize || 0);
  if (storedFirstPlacePrize > 0) return storedFirstPlacePrize;

  const storedFinalPrizePool = Number(tournament?.finalPrizePool || 0);
  const payouts = Array.isArray(tournament?.payouts) ? tournament.payouts : [];
  const firstPlace = payouts.find((p) => Number(p.place) === 1);

  if (!firstPlace) return 0;

  const prizePool =
    storedFinalPrizePool > 0
      ? storedFinalPrizePool
      : computeFallbackPrizePool(tournament);

  const percent = Number(firstPlace.percent || 0);
  return Math.round(prizePool * (percent / 100));
}

function TournamentCard({ tournament, onClick }) {
  const winnerName = tournament?.results?.winnerName || "—";
  const firstPlacePrize = computeFirstPlacePrize(tournament);
  const status = String(tournament?.status || "").toLowerCase();
  const buyIn = Number(tournament?.buyIn || 0);
  const isCustomChop =
    status === "completed" &&
    String(tournament?.results?.completionType || "").toLowerCase() ===
      "custom_chop";

  const statusStyles =
    status === "running"
      ? "bg-green-500/10 text-green-300 border-green-500/30"
      : status === "registering"
      ? "bg-blue-500/10 text-blue-300 border-blue-500/30"
      : status === "completed"
      ? "bg-zinc-700/30 text-zinc-200 border-zinc-700"
      : status === "cancelled"
      ? "bg-red-500/10 text-red-300 border-red-500/30"
      : "bg-zinc-700/30 text-zinc-200 border-zinc-700";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-zinc-100">
            {tournament?.name || "Untitled Tournament"}
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            {formatDate(tournament?.date)}
          </p>
        </div>

        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusStyles}`}
        >
          {status === "running"
            ? "Running"
            : status === "registering"
            ? "Registering"
            : status === "completed"
            ? isCustomChop
              ? "Completed • Custom Chop"
              : "Completed"
            : status === "cancelled"
            ? "Cancelled"
            : tournament?.status || "—"}
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Buy-In</p>
          <p className="text-zinc-100 font-semibold mt-1">
            {formatMoney(buyIn)}
          </p>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">
            {isCustomChop ? "1st Place" : "Winner"}
          </p>
          <p className="text-zinc-100 font-semibold mt-1">{winnerName}</p>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">
            1st Place Payout
          </p>
          <p className="text-zinc-100 font-semibold mt-1">
            {formatMoney(firstPlacePrize)}
          </p>
        </div>
      </div>
    </button>
  );
}

export default function Tournaments() {
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tRef = collection(db, "tournaments");
    const tQuery = query(tRef, orderBy("date", "desc"));

    const unsub = onSnapshot(
      tQuery,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setTournaments(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Load tournaments failed:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const activeTournaments = useMemo(() => {
    return tournaments.filter(
      (t) => t?.status === "registering" || t?.status === "running"
    );
  }, [tournaments]);

  const completedTournaments = useMemo(() => {
    return tournaments.filter((t) => t?.status === "completed");
  }, [tournaments]);

  if (loading) {
    return (
      <Layout>
        <p className="text-zinc-300">Loading tournaments...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: COPPER }}>
            Tournaments
          </h1>
          <p className="text-zinc-400 mt-2">
            Current tournaments appear at the top. Completed tournaments are listed below.
          </p>
        </div>

        <div className="mb-10">
          <h2 className="text-xl font-semibold text-zinc-100 mb-4">
            Current Tournaments
          </h2>

          {activeTournaments.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-zinc-400">
              No registering or running tournaments right now.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {activeTournaments.map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  onClick={() => navigate(`/tournament/${tournament.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl font-semibold text-zinc-100 mb-4">
            Completed Tournaments
          </h2>

          {completedTournaments.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-zinc-400">
              No completed tournaments yet.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {completedTournaments.map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  onClick={() => navigate(`/tournament/${tournament.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
